import { useEffect, useState } from 'react';
import { decodeMpegToPcmWav } from './decode-mpeg-to-pcm-wav';
import { unwrapMpegWav } from './unwrap-mpeg-wav';

const WAV_TYPES = new Set(['audio/wav', 'audio/wave', 'audio/x-wav']);

/**
 * Returns a URL/type the browser can actually play. MPEG-in-WAV is unwrapped;
 * MP2 is decoded to PCM WAV because native HTML audio cannot play Layer II.
 */
export function usePlayableAudioSrc(url: string | null, type: string | null) {
  const [src, setSrc] = useState<string | null>(null);
  const [playType, setPlayType] = useState<string | null>(null);

  useEffect(() => {
    if (!url || !type || !WAV_TYPES.has(type)) {
      setSrc(url);
      setPlayType(type);
      return;
    }

    setSrc(null);
    setPlayType(type);

    const objectUrls: string[] = [];
    let cancelled = false;

    fetch(url)
      .then((res) => res.arrayBuffer())
      .then((buffer) => {
        if (cancelled) {
          return;
        }
        const mpeg = unwrapMpegWav(buffer);
        if (mpeg) {
          // Layer III (MP3) plays natively. Layer I/II (MP2) must be decoded to PCM.
          const playAsMpeg = mpeg.layer === 3;
          const blobPromise = playAsMpeg
            ? Promise.resolve(mpeg.blob)
            : decodeMpegToPcmWav(mpeg.payload);
          return blobPromise
            .then((blob) => {
              if (cancelled) {
                return;
              }
              const objectUrl = URL.createObjectURL(blob);
              objectUrls.push(objectUrl);
              setSrc(objectUrl);
              setPlayType(playAsMpeg ? 'audio/mpeg' : 'audio/wav');
            })
            .catch(() => {
              if (cancelled) {
                return;
              }
              setSrc(url);
              setPlayType(type);
            });
        }
        setSrc(url);
        setPlayType(type);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setSrc(url);
        setPlayType(type);
      });

    return () => {
      cancelled = true;
      objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
    };
  }, [url, type]);

  // WAV sources stay empty until fetch/unwrap/decode finishes.
  const loading = Boolean(url && type && WAV_TYPES.has(type) && !src);

  return { src, playType, loading };
}
